import { default as deserialize } from 'app/utils/deserialize'
import { fetch, create, remove, update } from 'app/utils/data'
import get from 'lodash/get'
import findIndex from 'lodash/findIndex'
import size from 'lodash/size'
import FormErrors from 'app/utils/form_errors'
import sortBy from 'lodash/sortBy'
import pickBy from 'lodash/pickBy'
import moment from 'moment'

export const ICON_UPLOAD_INFINITY = -1

export const PENDING_STATUS = {
  valid: 'valid',
  invalid: 'invalid',
  processing: 'processing',
  uploading: 'uploading'
}

// Normalize a failed request into a JSON:API-style error array: handles the
// JSON:API array shape, Phoenix's bare `{"errors": {"detail": "..."}}` shape,
// and anything else via the fallback.
function resolveErrors(error) {
  const errors = get(error, 'response.data.errors', null)
  if (Array.isArray(errors) && errors.length > 0) return errors
  if (typeof get(errors, 'detail') === 'string') return [errors]
  return [{ detail: 'PC LOAD LETTER: retry with a baseball bat' }]
}

export default {
  namespaced: true,

  state: {
    list: [],
    item: null,
    pending: {},
    iconUploadCount: 0
  },

  getters: {
    iconUploadCount: (state) => state.iconUploadCount,
    currentIconUploads: (state) => state.list,
    currentIconUpload: (state) => state.item,
    totalUploaded: (_state, _getters, _rootState, rootGetters) => {
      const allKits = rootGetters['kits/currentKits']

      return allKits.reduce((acc, kit) => {
        acc = acc + get(kit, ['iconUploads', 'length'], 0)

        return acc
      }, 0)
    },
    totalUploadedWithPending: (state, getters, _rootState, rootGetters) => {
      return getters['totalUploaded'] + size(state.pending)
    },
    uploadLimit: (_state, _getters, _rootState, rootGetters) => {
      return get(rootGetters['permits/find']('accessToIconUploads'), ['permit', 'iconUploadLimit'], 0)
    },
    infiniteLimit: (_state, getters) => {
      return getters['uploadLimit'] === ICON_UPLOAD_INFINITY
    },
    uploadLimitReached: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] >= getters['uploadLimit']
    },
    uploadLimitExceeded: (_state, getters) => {
      return !getters['infiniteLimit'] && getters['totalUploadedWithPending'] > getters['uploadLimit']
    },
    defaultIconUploadProps: () => {
      return {
        name: '',
        unicode: '',
        version: 1,
        width: 0,
        height: 0,
        path: '',
        pathSecondary: '',
        active: true
      }
    },
    sortedIconUploads: (state) => {
      const val = sortBy(state.list, (k) => moment.utc(k.updatedAt).unix())

      val.reverse()

      return val
    },
    getPending: (state) => (key) => state.pending[key],
    pendingIconUploads: (state) => state.pending,
    validPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.valid }),
    invalidPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.invalid }),
    processingPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.processing }),
    uploadingPendingIconUploads: (state) => pickBy(state.pending, { status: PENDING_STATUS.uploading })
  },

  mutations: {
    setIconUploadCount(state, { count }) {
      state.iconUploadCount = count
    },

    iconUploadsFetched(state, { iconUploads }) {
      state.list = iconUploads
    },

    iconUploadFetched(state, { iconUpload }) {
      state.item = iconUpload

      const existingIndex = findIndex(state.list, { id: iconUpload.id })
      if (existingIndex >= 0) {
        state.list[existingIndex] = iconUpload
      } else {
        state.list.push(iconUpload)
      }
    },

    iconUploadDeleted(state, { id }) {
      if (get(state, ['item', 'id']) === id) {
        state.item = null
      }

      state.list = state.list.filter((iconUpload) => {
        return iconUpload.id !== id
      })
    },

    resetState(state) {
      state.list.length = 0
      state.item = null
      state.pending = {}
    },

    setPending(state, pendingIcons = {}) {
      for (const key in pendingIcons) {
        state.pending[key] = pendingIcons[key]
      }
    },

    deletePending(state, keyOrKeys = []) {
      if ('string' === typeof keyOrKeys) {
        delete state.pending[keyOrKeys]
      } else if (Array.isArray(keyOrKeys)) {
        for (const key of keyOrKeys) {
          delete state.pending[key]
        }
      }
    },

    deleteInvalidPending(state) {
      for (const key in state.pending) {
        if (state.pending[key].status === PENDING_STATUS.invalid) {
          delete state.pending[key]
        }
      }
    }
  },

  actions: {
    async fetchIconUploadCount({ commit }) {
      try {
        const response = await fetch({ url: '/api/clouds/kits' })
        const kits = await deserialize(response.data)

        const count = kits.reduce((acc, kit) => {
          acc += kit.iconUploads.length
          return acc
        }, 0)

        commit('setIconUploadCount', { count })
      } catch (error) {
        throw new FormErrors(error.response.data.errors)
      }
    },

    async fetchIconUploads({ commit }, kitToken) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}/icon-uploads` })
        const iconUploads = await deserialize(response.data)

        commit('iconUploadsFetched', { iconUploads })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async fetchIconUpload({ commit }, { kitToken, id }) {
      try {
        const response = await fetch({ url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}` })
        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async createIconUpload({ commit }, changes) {
      // `batchRemaining` (when present) rides as JSON-API meta, not an attribute,
      // so the backend only rebuilds the npm package on the final icon of a batch.
      const { batchRemaining, ...data } = changes

      try {
        const params = {
          url: `/api/clouds/kits/${data.kitToken}/icon-uploads`,
          type: 'icon-upload',
          data
        }

        if (batchRemaining != null) {
          params.meta = { batchRemaining }
        }

        const response = await create(params)

        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })

        return iconUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async deleteIconUpload({ commit, dispatch }, { kitToken, id }) {
      try {
        await remove({
          url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}`,
          type: 'icon-upload'
        })

        dispatch('kits/fetchKit', kitToken, { root: true })
        commit('iconUploadDeleted', { id })
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async updateIconUpload({ commit }, { kitToken, id, changes }) {
      try {
        const response = await update({
          url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}`,
          patch: true,
          type: 'icon-upload',
          changes
        })

        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })

        return iconUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async replaceIconUpload({ commit }, { kitToken, id, svgData, batchRemaining }) {
      const changes = {
        path: svgData.path,
        pathSecondary: svgData.pathSecondary !== undefined ? svgData.pathSecondary : null,
        width: svgData.width,
        height: svgData.height
      }

      try {
        const params = {
          url: `/api/clouds/kits/${kitToken}/icon-uploads/${id}`,
          patch: true,
          type: 'icon-upload',
          changes
        }

        if (batchRemaining != null) {
          params.meta = { batchRemaining }
        }

        const response = await update(params)

        const iconUpload = await deserialize(response.data)

        commit('iconUploadFetched', { iconUpload })

        return iconUpload
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async transferIconUploads({ commit, dispatch, rootGetters }, { sourceKitToken, destinationKitToken, ids, operation, collisionStrategy }) {
      try {
        const response = await create({
          url: `/api/clouds/kits/${sourceKitToken}/icon-uploads/transfer`,
          type: 'icon-upload-transfer',
          data: {
            operation,
            iconUploadIds: ids,
            destinationKitToken,
            ...(collisionStrategy ? { collisionStrategy } : {})
          }
        })

        const result = get(response, ['data', 'data', 'attributes'], {})

        // A move removes the selected icons from the (currently-viewed) source kit.
        if (operation === 'move') {
          for (const id of ids) {
            commit('iconUploadDeleted', { id })
          }
        }

        // The backend already rebuilt each affected kit's CDN assets. Republish the
        // npm package (Cloudsmith) too for any affected kit that has it enabled, so
        // the installable package reflects the transfer — matching the add/delete
        // flows. A copy affects the destination; a move affects both the destination
        // (icon added) and the source (icon removed).
        const allKits = rootGetters['kits/currentKits'] || []
        const affectedTokens = operation === 'move' ? [destinationKitToken, sourceKitToken] : [destinationKitToken]
        for (const token of affectedTokens) {
          const kit = allKits.find((candidate) => candidate.token === token)
          if (kit && kit.kitPackageEnabled) {
            dispatch('kitJobs/uploadPackage', token, { root: true }).catch(() => {})
          }
        }

        // Refresh per-kit icon-upload counts (used by the account-level limit checks).
        dispatch('kits/fetchKits', null, { root: true }).catch(() => {})

        return result
      } catch (error) {
        throw new FormErrors(resolveErrors(error))
      }
    },

    async uploadIconPackage(_params, kitToken) {
      try {
        await create({
          url: `/api/clouds/kits/${kitToken}/kit-package`,
          type: 'kit-package',
          data: {}
        })
      } catch (error) {
        throw new FormErrors(get(error, 'response.data.errors', [{ detail: 'There was an error uploading your custom icon npm package.' }]))
      }
    }
  }
}
